import { Document, documentDTO } from './data-model.document';
import { DocumentStore } from './document-store';

/**
 * Manages a collection of DocumentStore instances keyed by element ID.
 *
 * Replaces the conceptual role of ElementStateStore (content script) and
 * provides a shared foundation usable in any plain-TS context.  No RxJS,
 * no Angular — deliberately dependency-free.
 */
export class MultiElementDocumentStore {
  private readonly stores = new Map<string, DocumentStore>();

  /**
   * Return the DocumentStore for the given element ID, creating it with
   * an optional initial state if it does not exist yet.
   * If no initial state is provided the store is seeded with an empty document.
   */
  getOrCreate(elId: string, initial?: documentDTO): DocumentStore {
    if (!this.stores.has(elId)) {
      const seed = initial ?? Document.fromString('', 0, elId).toDTO();
      this.stores.set(elId, new DocumentStore(seed));
    }
    return this.stores.get(elId)!;
  }

  /** Return the DocumentStore for the given element ID, or undefined if absent. */
  get(elId: string): DocumentStore | undefined {
    return this.stores.get(elId);
  }

  /** Return true if a store exists for the given element ID. */
  has(elId: string): boolean {
    return this.stores.has(elId);
  }

  /** Remove the store for the given element ID. */
  delete(elId: string): void {
    this.stores.delete(elId);
  }

  /** Remove all stores. */
  clear(): void {
    this.stores.clear();
  }
}
